import { Placeholder } from '#~/audio/components/card/index.js';
import { useDeviceIds, useRoom } from '#~/room/hooks.js';
import { Sel, STag } from '#~/stag/tag.js';
import { Layout } from './constants.js';
import { Item } from './item/index.js';
import { Output } from './output.jsx';
import { Selection } from './selection.jsx';
import type { Props } from './types.js';
import { useWiring } from './wiring.js';
import { Anchor, Ikon } from '@studio/design-system/button';
import { Column } from '@studio/design-system/form';
import { ensureNotNil } from '@studio/js-utils';
import { normalizeId } from '@studio/models';
import { toDeviceId } from '@studio/models/device';
import { useLatestFn, useLazyRef } from '@studio/react-utils/hooks';
import type { T_NotNil } from '@tryiris-ai/js-types';
import { pick } from 'lodash-es';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { twMerge } from 'cn';

/** Audio `<Sources>` component. */
export const Sources = ({ className, ...props }: Props): ReactElement => {
  const devices = useRoom((µ) => µ.devices);
  const { audioIds } = useDeviceIds();
  const { selectedId, ...wiring } = useWiring(devices);
  const device = selectedId ? devices[selectedId] : undefined;

  const available = useMemo(
    () => audioIds.map((µ) => devices[µ]).filter((µ): µ is T_NotNil<typeof µ> => !!µ),
    [audioIds, devices]
  );

  const handleValueChange = useLatestFn((nextValue: string) => {
    const nextId = normalizeId(nextValue);

    if (nextId && nextId !== selectedId) {
      wiring.handleSelect(toDeviceId(nextId));
    }
  });

  // Wiring handlers are stable so this is okay!
  const handler = useLazyRef(() => ({
    onRemove: wiring.handleRemove,
    onRename: wiring.handleRename,
    onSettings: wiring.handleSettings
  })).current;

  return (
    <Column className={twMerge('py-16', className)} {...props}>
      {!!device && (
        <>
          <Output className={Layout.marginX} deviceId={device.id} />
          <hr />
        </>
      )}

      <div>
        <Selection
          available={available}
          className={Layout.marginX}
          onValueChange={handleValueChange}
          selectedId={selectedId}
        />

        {props.canControl && !available.length && (
          <p
            className={twMerge(
              'select-none text-justify text-sm font-regular text-secondary',
              'pt-8 [&>:is(a,button)]:text-foreground',
              Layout.marginX
            )}
          >
            Operating remotely? <Anchor onClick={wiring.handleAdd}>Add</Anchor> a reference audio
            source.
            {/*
            More information on how we handle audio can be found &nbsp;
            <a href='https://tryiris.ai/help' target='__blank'>
              here
            </a>
            . */}
          </p>
        )}
      </div>

      {device ? (
        <>
          {/* Be very careful w/ memoization here! */}
          <Item
            className={Layout.marginX}
            device={pick(device, ['id', 'name'])}
            handler={handler}
            status={ensureNotNil(device.audio).status}
            {...STag(Sel.MainRoomLeftTrayAudio, 'devices', device.id)}
          />
          <hr />
        </>
      ) : (
        !!props.canControl && (
          /* Safari needs this wrapper `div`. */
          <div className={Layout.marginX}>
            <Placeholder onClick={wiring.handleAdd} />
          </div>
        )
      )}

      {props.canControl && (
        <Ikon
          className='w-32 self-center text-lg'
          icon='plus'
          onClick={wiring.handleAdd}
          {...STag(Sel.MainRoomLeftTrayAudio, 'footer.add')}
        />
      )}
    </Column>
  );
};
